import React, { useState, useEffect } from "react";
import CustomModal from "../../../Global/Modal/CustomModal";
import { Card, Button, Form } from "react-bootstrap";
import {
  FaPlus,
  FaCopy,
  FaEdit,
  FaCheck,
  FaTrash,
  FaChartBar,
  FaEye,
  FaTrash as FaTrashIcon,
  FaGripVertical,
  FaBox,
  FaArrowRight,
} from "react-icons/fa";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import {
  submitEvaluationTemplate,
  updateDefaultTemplate,
  updateTemplateArchiveStatus,
} from "../../../../backend/firebaseFunctions";
import TipCard from "../../Robuts Components/TipCard";

const FeedbackTemplatesModal = ({
  isOpen,
  onClose,
  evaluationTemplates = [],
  setEvaluationTemplates,
  userID,
  activateAlert,
}) => {
  // Evaluation template states
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingParameter, setEditingParameter] = useState(null);
  const [editingParameterName, setEditingParameterName] = useState("");
  const [editingParameterType, setEditingParameterType] = useState("scale");
  const [confirmDeleteCategoryId, setConfirmDeleteCategoryId] = useState(null);
  const [confirmDeleteParameterId, setConfirmDeleteParameterId] =
    useState(null);
  const [lastCreatedCategoryId, setLastCreatedCategoryId] = useState(null);
  // Show template builder UI
  const [showTemplateBuilder, setShowTemplateBuilder] = useState(false);
  const [isViewOnly, setIsViewOnly] = useState(false);
  // Template name state
  const [templateName, setTemplateName] = useState("");
  // Track if there are unsaved changes in the template builder
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  // Show unsaved changes modal for template builder return button
  const [showTemplateBuilderUnsavedModal, setShowTemplateBuilderUnsavedModal] =
    useState(false);
  const [isSavingTemplate, setIsSavingTemplate] = useState(false);

  // Add a new state for the staged template
  const [stagedTemplate, setStagedTemplate] = useState([]);

  // Add function to handle template duplication
  const handleDuplicateTemplate = (template) => {
    const newStagedTemplate = [
      {
        templateType: "coach",
        categories: template.coachTemplate.categories.map((cat) => ({
          ...cat,
          id:
            Date.now().toString() +
            "_" +
            Math.random().toString(36).substr(2, 9),
        })),
      },
      {
        templateType: "student",
        categories: template.athleteTemplate.categories.map((cat) => ({
          ...cat,
          id:
            Date.now().toString() +
            "_" +
            Math.random().toString(36).substr(2, 9),
        })),
      },
    ];
    setStagedTemplate(newStagedTemplate);
    setTemplateName(template.name);
    setShowTemplateBuilder(true);
  };

  // Modify the save button handler
  const handleSaveTemplate = async () => {
    if (!templateName.trim()) {
      activateAlert("warning", "שגיאה", "נא להזין שם לתבנית");
      return;
    }
    const now = new Date();
    const latestVersion = evaluationTemplates.reduce((maxVersion, template) => {
      return Math.max(maxVersion, template.version || 1);
    }, 0);
    const formattedData = {
      name: templateName.trim(),
      version: latestVersion + 1,
      createdAt: now, // Firebase will automatically convert this to Timestamp
      coachTemplate: {
        categories:
          stagedTemplate.find((t) => t.templateType === "coach")?.categories ||
          [],
      },
      athleteTemplate: {
        categories:
          stagedTemplate.find((t) => t.templateType === "student")
            ?.categories || [],
      },
    };
    setIsSavingTemplate(true);
    try {
      const result = await submitEvaluationTemplate(userID, formattedData);
      if (result && result.templateId) {
        const newTemplate = {
          ...formattedData,
          templateId: result.templateId,
          createdAt: now, // Keep as JS Date for local state
        };
        setEvaluationTemplates([...evaluationTemplates, newTemplate]);
        setShowTemplateBuilder(false);
        setTemplateName("");
        setStagedTemplate([]); // Reset staged template
        activateAlert("success", "תבנית נשמרה", "התבנית נשמרה בהצלחה");
      } else {
        throw new Error("שמירת התבנית נכשלה");
      }
    } catch (error) {
      activateAlert("danger", "שגיאה", "אירעה שגיאה בשמירת התבנית. נסה שוב.");
      console.error(error);
    } finally {
      setIsSavingTemplate(false);
    }
  };

  // Separate states for coach and athlete sections
  const [coachNewCategoryName, setCoachNewCategoryName] = useState("");
  const [athleteNewCategoryName, setAthleteNewCategoryName] = useState("");

  // Add new state for tracking new parameters per category
  const [newParameters, setNewParameters] = useState({});

  // Helper function to get/set new parameter state for a category
  const getNewParameterState = (categoryId) => {
    if (!newParameters[categoryId]) {
      setNewParameters((prev) => ({
        ...prev,
        [categoryId]: { name: "", type: "scale" },
      }));
    }
    return newParameters[categoryId] || { name: "", type: "scale" };
  };

  const setNewParameterState = (categoryId, field, value) => {
    setNewParameters((prev) => ({
      ...prev,
      [categoryId]: {
        ...(prev[categoryId] || { name: "", type: "scale" }),
        [field]: value,
      },
    }));
  };

  // Detect unsaved changes in the template builder
  useEffect(() => {
    if (!showTemplateBuilder) {
      setHasUnsavedChanges(false);
      return;
    }
    // Check if there are any changes (templateName, categories, or parameters)
    const coach = stagedTemplate.find((t) => t.templateType === "coach");
    const athlete = stagedTemplate.find((t) => t.templateType === "student");
    const hasCoachCategories =
      coach && coach.categories && coach.categories.length > 0;
    const hasCoachParams =
      hasCoachCategories &&
      coach.categories.some(
        (cat) => cat.parameters && cat.parameters.length > 0
      );
    const hasAthleteCategories =
      athlete && athlete.categories && athlete.categories.length > 0;
    const hasAthleteParams =
      hasAthleteCategories &&
      athlete.categories.some(
        (cat) => cat.parameters && cat.parameters.length > 0
      );
    // If any field is filled, mark as unsaved
    if (
      templateName.trim() ||
      hasCoachCategories ||
      hasCoachParams ||
      hasAthleteCategories ||
      hasAthleteParams
    ) {
      setHasUnsavedChanges(true);
    } else {
      setHasUnsavedChanges(false);
    }
  }, [showTemplateBuilder, templateName, stagedTemplate]);

  // Add beforeunload event for unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = "";
        return "";
      }
    };
    if (hasUnsavedChanges) {
      window.addEventListener("beforeunload", handleBeforeUnload);
    } else {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    }
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  const renderEvaluationSettings = () => {
    // Filter templates based on archive status
    const activeTemplates = evaluationTemplates.filter(
      (template) => !template.isArchived
    );
    const archivedTemplates = evaluationTemplates.filter(
      (template) => template.isArchived
    );
    const displayedTemplates = showArchivedTemplates
      ? archivedTemplates
      : activeTemplates;

    // Only allow 'coach' or 'student' as templateType
    const renderEvaluationSection = (type) => {
      // Map 'athlete' to 'student' for backward compatibility (if needed)
      const normalizedType = type === "athlete" ? "student" : type;
      const typeLabel = normalizedType === "coach" ? "מאמן" : "ספורטאי";
      const template = stagedTemplate.find(
        (t) => t.templateType === normalizedType
      );
      const categories = template?.categories || [];

      // Get the appropriate state setters based on type
      const newCategoryName =
        type === "coach" ? coachNewCategoryName : athleteNewCategoryName;
      const setNewCategoryName =
        type === "coach" ? setCoachNewCategoryName : setAthleteNewCategoryName;

      // Color styles for coach/athlete
      const categoryBg = normalizedType === "coach" ? "#e3f0ff" : "#fff3e0";
      const paramBg = normalizedType === "coach" ? "#f5faff" : "#fff8e1";

      // Helper for add category
      function handleAddCategory() {
        if (!newCategoryName.trim()) return;
        const newCategory = {
          id: Date.now().toString(),
          name: newCategoryName.trim(),
          parameters: [],
        };
        setLastCreatedCategoryId(newCategory.id);
        const existingTemplate = stagedTemplate.find(
          (t) => t.templateType === normalizedType
        );
        let updatedTemplates;
        if (existingTemplate) {
          updatedTemplates = stagedTemplate.map((t) =>
            t.templateType === normalizedType
              ? {
                ...t,
                categories: [...t.categories, newCategory],
              }
              : t
          );
        } else {
          const newTemplate = {
            templateId: `template_${Date.now()}`,
            templateType: normalizedType,
            version: 1,
            label: `תבנית ${typeLabel}`,
            active: true,
            createdAt: new Date(),
            createdBy: userID,
            categories: [newCategory],
          };
          updatedTemplates = [
            ...stagedTemplate.filter((t) => t.templateType !== normalizedType),
            newTemplate,
          ];
        }
        setStagedTemplate(updatedTemplates);
        setNewCategoryName("");
      }

      // Add the handleAddParameter function
      const handleAddParameter = (categoryId, templateType) => {
        const { name, type } = getNewParameterState(categoryId);
        if (!name.trim()) return;
        const normalizedType =
          templateType === "athlete" ? "student" : templateType;
        const newParameter = {
          id:
            Date.now().toString() +
            "_" +
            Math.random().toString(36).substr(2, 9),
          name: name.trim(),
          inputType: type,
        };
        const newTemplates = stagedTemplate.map((t) => {
          if (t.templateType === normalizedType) {
            return {
              ...t,
              categories: t.categories.map((c) =>
                c.id === categoryId
                  ? {
                    ...c,
                    parameters: [...(c.parameters || []), newParameter],
                  }
                  : c
              ),
            };
          }
          return t;
        });
        setStagedTemplate(newTemplates);
        setNewParameterState(categoryId, "name", "");
        setNewParameterState(categoryId, "type", "scale");
      };

      return (
        <Card
          className="evaluation-settings mb-4 shadow-sm"
          style={{
            borderRadius: "12px",
            border: "none",
            background: categoryBg,
          }}
        >
          <Card.Header className="bg-white border-0 pt-4 pb-2">
            <h4 className="mb-0 text-primary" style={{ fontWeight: 800 }}>
              משוב {typeLabel} {typeLabel === "מאמן" ? "*" : ""}
            </h4>
            <h6 className="mb-0 text-primary" style={{ fontWeight: 600 }}>
              {typeLabel === "מאמן"
                ? "לפחות קטגוריה אחת ופרמטר אחד"
                : "לא חובה"}
            </h6>
          </Card.Header>
          <Card.Body>
            {!isViewOnly && (
              <div
                className="add-category-sectio rounded-3"
                style={{ transition: "all 0.3s ease" }}
              >
                <div
                  className="d-flex flex-row-reverse align-items-center gap-2 justify-content-end"
                  style={{ width: "100%" }}
                >
                  <Button
                    variant="success"
                    disabled={!newCategoryName.trim()}
                    onClick={handleAddCategory}
                    className="rounded-pill fw-bold d-flex align-items-center"
                    style={{
                      fontSize: "1rem",
                      transition: "all 0.2s",
                      opacity: newCategoryName.trim() ? 1 : 0.6,
                      boxShadow: "0 2px 8px rgba(0,180,80,0.08)",
                      margin: "20px 20px",
                      paddingLeft: "20px",
                      width: "auto",
                    }}
                    onMouseOver={(e) =>
                      (e.currentTarget.style.transform = "translateY(-2px)")
                    }
                    onMouseOut={(e) =>
                      (e.currentTarget.style.transform = "none")
                    }
                  >
                    <FaPlus className="ms-2" />
                    הוסף קטגוריה
                  </Button>
                  <div style={{ position: "relative", width: "240px" }}>
                    <Form.Control
                      type="text"
                      placeholder="שם קטגוריה חדשה"
                      value={newCategoryName}
                      onChange={(e) =>
                        setNewCategoryName(e.target.value.slice(0, 20))
                      }
                      className="border-0 shadow-sm"
                      style={{
                        width: "100%",
                        height: "40px",
                        borderRadius: "8px",
                        fontSize: "1rem",
                        direction: "rtl",
                        textAlign: "right",
                        background: "#fff",
                        transition: "box-shadow 0.2s",
                        paddingRight: "48px",
                      }}
                      maxLength={20}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && newCategoryName.trim())
                          handleAddCategory();
                      }}
                    />
                    <span
                      style={{
                        position: "absolute",
                        top: "50%",
                        right: "12px",
                        transform: "translateY(-50%)",
                        fontSize: "0.95em",
                        color: "#888",
                        pointerEvents: "none",
                      }}
                    >
                      {newCategoryName.length}/20
                    </span>
                  </div>
                </div>
              </div>
            )}

            {categories.length === 0 ? (
              <div
                className="empty-state d-flex flex-column align-items-center justify-content-center"
                style={{ minHeight: "180px", width: "100%" }}
              >
                <FaChartBar size={48} className="text-muted opacity-50 mb-3" />
                <h5 className="text-muted mb-1" style={{ fontWeight: 500 }}>
                  אין קטגוריות מוגדרות
                </h5>
                <p
                  className="text-muted mb-0"
                  style={{ fontSize: "0.95rem", fontWeight: 400 }}
                >
                  הזן שם קטגוריה חדשה כדי להתחיל
                </p>
              </div>
            ) : (
              <div className="categories-section">
                {categories.map((category) => (
                  <Card
                    key={category.id}
                    id={`category-${category.id}`}
                    className="mb-3 border-0 shadow-sm"
                    style={{
                      borderRadius: "10px",
                      transition: "all 0.2s ease",
                      ":hover": { transform: "translateY(-2px)" },
                    }}
                  >
                    <Card.Body className="p-3">
                      <div className="d-flex justify-content-start align-items-center mb-3">
                        {editingCategory === category.id ? (
                          <div
                            className="d-flex align-items-center gap-2"
                            style={{ width: "100%" }}
                          >
                            <Form.Control
                              type="text"
                              value={editingCategoryName}
                              onChange={(e) =>
                                setEditingCategoryName(e.target.value)
                              }
                              className="border-0 shadow-sm"
                              style={{
                                width: "200px",
                                height: "38px",
                                borderRadius: "8px",
                                fontSize: "1rem",
                                direction: "rtl",
                                textAlign: "right",
                              }}
                              autoFocus
                              onKeyDown={(e) => {
                                if (
                                  e.key === "Enter" &&
                                  editingCategoryName.trim()
                                ) {
                                  handleCategoryNameChange(
                                    category.id,
                                    editingCategoryName.trim(),
                                    normalizedType
                                  );
                                }
                              }}
                            />
                            <Button
                              variant="success"
                              size="sm"
                              onClick={() =>
                                handleCategoryNameChange(
                                  category.id,
                                  editingCategoryName.trim(),
                                  normalizedType
                                )
                              }
                              disabled={!editingCategoryName.trim()}
                              style={{
                                fontSize: "0.85rem",
                                borderRadius: "6px",
                                minWidth: "80px",
                              }}
                            >
                              שמור
                            </Button>
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => {
                                setEditingCategory(null);
                                setEditingCategoryName("");
                              }}
                              style={{
                                fontSize: "0.85rem",
                                borderRadius: "6px",
                                minWidth: "80px",
                              }}
                            >
                              ביטול
                            </Button>
                          </div>
                        ) : (
                          <>
                            <h6
                              className="mb-0"
                              style={{ fontWeight: "800", marginLeft: "20px" }}
                            >
                              קטגוריית {category.name}
                            </h6>
                            {!isViewOnly && (
                              <div className="d-flex gap-2">
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={() =>
                                    handleStartEditingCategory(category)
                                  }
                                  style={{
                                    fontSize: "0.85rem",
                                    borderRadius: "6px",
                                    width: "auto",
                                    minWidth: "160px",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  שנה שם קטגוריה{" "}
                                  <FaEdit style={{ marginRight: 6 }} />
                                </Button>
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={() =>
                                    handleDuplicateCategory(
                                      category,
                                      category.templateType
                                    )
                                  }
                                  style={{
                                    fontSize: "0.85rem",
                                    borderRadius: "6px",
                                    width: "auto",
                                    minWidth: "160px",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  שכפל קטגוריה
                                  <FaCopy style={{ marginRight: 6 }} />
                                </Button>
                                {confirmDeleteCategoryId === category.id ? (
                                  <span
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 8,
                                    }}
                                  >
                                    <span
                                      style={{
                                        color: "#d32f2f",
                                        fontWeight: 500,
                                        fontSize: "0.95em",
                                        minWidth: "250px",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      אתה בטוח שברצונך למחוק את הקטגוריה?
                                    </span>
                                    <Button
                                      variant="outline-secondary"
                                      size="sm"
                                      style={{
                                        fontSize: "0.85rem",
                                        padding: "0.25rem 0.75rem",
                                        borderRadius: "6px",
                                      }}
                                      onClick={() =>
                                        setConfirmDeleteCategoryId(null)
                                      }
                                    >
                                      ביטול
                                    </Button>
                                    <Button
                                      variant="danger"
                                      size="sm"
                                      style={{
                                        fontSize: "0.85rem",
                                        padding: "0.25rem 0.75rem",
                                        borderRadius: "6px",
                                      }}
                                      onClick={() => {
                                        const newTemplates = stagedTemplate.map(
                                          (t) =>
                                            t.templateType === normalizedType
                                              ? {
                                                ...t,
                                                categories:
                                                  t.categories.filter(
                                                    (c) =>
                                                      c.id !== category.id
                                                  ),
                                              }
                                              : t
                                        );
                                        setStagedTemplate(newTemplates);
                                        setConfirmDeleteCategoryId(null);
                                      }}
                                    >
                                      מחק
                                    </Button>
                                  </span>
                                ) : (
                                  <Button
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={() =>
                                      setConfirmDeleteCategoryId(category.id)
                                    }
                                    style={{
                                      fontSize: "0.85rem",
                                      borderRadius: "6px",
                                      minWidth: "140px",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    מחק קטגוריה{" "}
                                    <FaTrashIcon style={{ marginRight: 6 }} />
                                  </Button>
                                )}
                              </div>
                            )}
                          </>
                        )}
                      </div>

                      {!isViewOnly && (
                        <div className="add-parameter-section d-flex gap-2 mt-3 mb-3">
                          <Form.Control
                            type="text"
                            placeholder="שם פרמטר חדש"
                            value={getNewParameterState(category.id).name}
                            onChange={(e) =>
                              setNewParameterState(
                                category.id,
                                "name",
                                e.target.value
                              )
                            }
                            className="border-0 shadow-sm"
                            style={{
                              width: "250px",
                              height: "38px",
                              borderRadius: "8px",
                              fontSize: "1rem",
                              direction: "rtl",
                              textAlign: "right",
                              background: "#fff",
                              transition: "box-shadow 0.2s",
                            }}
                            onKeyDown={(e) => {
                              if (
                                e.key === "Enter" &&
                                getNewParameterState(category.id).name.trim()
                              ) {
                                handleAddParameter(category.id, normalizedType);
                              }
                            }}
                          />
                          <Form.Select
                            value={getNewParameterState(category.id).type}
                            onChange={(e) =>
                              setNewParameterState(
                                category.id,
                                "type",
                                e.target.value
                              )
                            }
                            className="border-0 shadow-sm text-muted"
                            style={{
                              width: "120px",
                              height: "38px",
                              borderRadius: "8px",
                              fontSize: "1rem",
                            }}
                          >
                            <option value="scale">סקאלה</option>
                            <option value="number">מספר</option>
                            <option value="text">טקסט</option>
                            <option value="yesOrNo">כן/לא</option>
                          </Form.Select>
                          <Button
                            variant="success"
                            size="sm"
                            onClick={() =>
                              handleAddParameter(category.id, normalizedType)
                            }
                            disabled={
                              !getNewParameterState(category.id).name.trim()
                            }
                            className="rounded-pill shadow-sm fw-bold d-flex align-items-center"
                            style={{
                              fontSize: "0.85rem",
                              transition: "all 0.2s",
                              opacity: getNewParameterState(
                                category.id
                              ).name.trim()
                                ? 1
                                : 0.6,
                              boxShadow: "0 2px 8px rgba(0,180,80,0.08)",
                              margin: "0 10px",
                              padding: "0 10px",
                              width: "auto",
                            }}
                            onMouseOver={(e) =>
                            (e.currentTarget.style.transform =
                              "translateY(-2px)")
                            }
                            onMouseOut={(e) =>
                              (e.currentTarget.style.transform = "none")
                            }
                          >
                            <FaPlus className="ms-2" />
                            הוסף פרמטר
                          </Button>
                        </div>
                      )}

                      <div className="parameters-section">
                        {!isViewOnly ? (
                          // Editable mode: enable drag-and-drop
                          <DragDropContext
                            onDragEnd={(result) =>
                              handleParameterDragEnd(
                                result,
                                category.id,
                                normalizedType
                              )
                            }
                          >
                            <Droppable
                              droppableId={`droppable-${category.id}`}
                              direction="vertical"
                            >
                              {(provided, snapshot) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.droppableProps}
                                  style={{ minHeight: "10px" }}
                                >
                                  {/* Table-like header for parameters - only if parameters exist */}
                                  {category.parameters.length > 0 && (
                                    <div
                                      className="d-flex align-items-center mb-2 px-2"
                                      style={{
                                        fontWeight: 700,
                                        fontSize: "0.98rem",
                                        color: "#6c757d",
                                        background: "transparent",
                                        minHeight: "32px",
                                      }}
                                    >
                                      <div
                                        style={{ flex: 1, textAlign: "right" }}
                                      >
                                        שם פרמטר
                                      </div>
                                      <div
                                        style={{ flex: 1, textAlign: "right" }}
                                      >
                                        סוג מידע
                                      </div>
                                      <div
                                        style={{ flex: 1, textAlign: "right" }}
                                      >
                                        {/* Actions */}
                                      </div>
                                    </div>
                                  )}
                                  {category.parameters.map((parameter, idx) => (
                                    <Draggable
                                      key={parameter.id}
                                      draggableId={parameter.id}
                                      index={idx}
                                    >
                                      {(provided, snapshot) => (
                                        <div
                                          ref={provided.innerRef}
                                          {...provided.draggableProps}
                                          className="parameter-item d-flex align-items-center mb-2 p-2 rounded-3"
                                          style={{
                                            backgroundColor: paramBg,
                                            transition: "all 0.2s ease",
                                            minHeight: "44px",
                                            ...provided.draggableProps.style,
                                            boxShadow: snapshot.isDragging
                                              ? "0 2px 8px rgba(0,0,0,0.15)"
                                              : undefined,
                                          }}
                                        >
                                          {/* Drag handle */}
                                          <span
                                            {...provided.dragHandleProps}
                                            style={{
                                              cursor: "grab",
                                              marginLeft: 12,
                                              marginRight: 8,
                                              color: "#888",
                                              display: "flex",
                                              alignItems: "center",
                                              fontSize: "1.4em",
                                            }}
                                            title="גרור לשינוי סדר"
                                          >
                                            <FaGripVertical />
                                          </span>
                                          {/* ... existing code for parameter row ... */}
                                          <div
                                            style={{
                                              flex: 1,
                                              textAlign: "right",
                                              display: "flex",
                                              alignItems: "center",
                                            }}
                                          >
                                            <span
                                              className="me-2"
                                              style={{
                                                fontWeight: "800",
                                                marginLeft: "10px",
                                              }}
                                            >
                                              {parameter.name}
                                            </span>
                                          </div>
                                          <div
                                            style={{
                                              flex: 1,
                                              textAlign: "center",
                                              display: "flex",
                                              alignItems: "center",
                                              justifyContent: "center",
                                            }}
                                          >
                                            <small
                                              className="text-muted"
                                              style={{
                                                width: "100%",
                                                textAlign: "right",
                                              }}
                                            >
                                              {parameter.inputType === "scale"
                                                ? "סקאלה"
                                                : parameter.inputType ===
                                                  "number"
                                                  ? "מספר"
                                                  : parameter.inputType ===
                                                    "yesOrNo"
                                                    ? "כן/לא"
                                                    : "טקסט"}
                                            </small>
                                          </div>
                                          {/* Actions (edit/delete) */}
                                          <div
                                            style={{
                                              flex: 1,
                                              textAlign: "right",
                                              justifyContent: "flex-end",
                                            }}
                                            className="d-flex gap-2"
                                          >
                                            {editingParameter ===
                                              parameter.id ? (
                                              <div className="d-flex gap-2 align-items-center">
                                                <Form.Control
                                                  type="text"
                                                  value={editingParameterName}
                                                  onChange={(e) =>
                                                    setEditingParameterName(
                                                      e.target.value.slice(
                                                        0,
                                                        20
                                                      )
                                                    )
                                                  }
                                                  className="border-0 shadow-sm"
                                                  style={{
                                                    width: "200px",
                                                    height: "38px",
                                                    borderRadius: "8px",
                                                    fontSize: "1rem",
                                                    direction: "rtl",
                                                    textAlign: "right",
                                                  }}
                                                  autoFocus
                                                />
                                                <Form.Select
                                                  value={editingParameterType}
                                                  onChange={(e) =>
                                                    setEditingParameterType(
                                                      e.target.value
                                                    )
                                                  }
                                                  className="border-0 shadow-sm text-muted"
                                                  style={{
                                                    width: "120px",
                                                    height: "38px",
                                                    borderRadius: "8px",
                                                    fontSize: "1rem",
                                                  }}
                                                >
                                                  <option value="scale">
                                                    סקאלה
                                                  </option>
                                                  <option value="number">
                                                    מספר
                                                  </option>
                                                  <option value="text">
                                                    טקסט
                                                  </option>
                                                  <option value="yesOrNo">
                                                    כן/לא
                                                  </option>
                                                </Form.Select>
                                                <Button
                                                  variant="success"
                                                  size="sm"
                                                  onClick={() => {
                                                    if (
                                                      !editingParameterName.trim()
                                                    ) {
                                                      activateAlert(
                                                        "warning",
                                                        "שגיאה",
                                                        "נא להזין שם לפרמטר"
                                                      );
                                                      return;
                                                    }
                                                    const newTemplates =
                                                      stagedTemplate.map((t) =>
                                                        t.templateType ===
                                                          normalizedType
                                                          ? {
                                                            ...t,
                                                            categories:
                                                              t.categories.map(
                                                                (c) =>
                                                                  c.id ===
                                                                    category.id
                                                                    ? {
                                                                      ...c,
                                                                      parameters:
                                                                        c.parameters.map(
                                                                          (
                                                                            p
                                                                          ) =>
                                                                            p.id ===
                                                                              parameter.id
                                                                              ? {
                                                                                ...p,
                                                                                name: editingParameterName.trim(),
                                                                                inputType:
                                                                                  editingParameterType,
                                                                              }
                                                                              : p
                                                                        ),
                                                                    }
                                                                    : c
                                                              ),
                                                          }
                                                          : t
                                                      );
                                                    setStagedTemplate(
                                                      newTemplates
                                                    );
                                                    setEditingParameter(null);
                                                    setEditingParameterName("");
                                                    setEditingParameterType(
                                                      "scale"
                                                    );
                                                  }}
                                                  style={{
                                                    fontSize: "0.85rem",
                                                    borderRadius: "6px",
                                                    minWidth: "80px",
                                                  }}
                                                >
                                                  שמור
                                                </Button>
                                                <Button
                                                  variant="outline-secondary"
                                                  size="sm"
                                                  onClick={() => {
                                                    setEditingParameter(null);
                                                    setEditingParameterName("");
                                                    setEditingParameterType(
                                                      "scale"
                                                    );
                                                  }}
                                                  style={{
                                                    fontSize: "0.85rem",
                                                    borderRadius: "6px",
                                                    minWidth: "80px",
                                                  }}
                                                >
                                                  ביטול
                                                </Button>
                                              </div>
                                            ) : confirmDeleteParameterId ===
                                              parameter.id ? (
                                              <span
                                                style={{
                                                  display: "flex",
                                                  flexDirection: "column",
                                                  alignItems: "flex-start",
                                                  width: "100%",
                                                }}
                                              >
                                                <span
                                                  style={{
                                                    color: "#d32f2f",
                                                    fontWeight: 500,
                                                    fontSize: "0.95em",
                                                    maxWidth: "220px",
                                                    marginBottom: 2,
                                                    whiteSpace: "normal",
                                                    textAlign: "right",
                                                  }}
                                                >
                                                  אתה בטוח שברצונך למחוק את
                                                  הפרמטר?
                                                </span>
                                                <span
                                                  style={{
                                                    display: "flex",
                                                    flexDirection:
                                                      "row-reverse",
                                                    alignItems: "center",
                                                    gap: 8,
                                                  }}
                                                >
                                                  <Button
                                                    variant="danger"
                                                    style={{
                                                      fontSize: "0.85rem",
                                                      padding:
                                                        "0.25rem 0.75rem",
                                                      borderRadius: "6px",
                                                      minWidth: 56,
                                                    }}
                                                    onClick={() => {
                                                      const newTemplates =
                                                        stagedTemplate.map(
                                                          (t) =>
                                                            t.templateType ===
                                                              normalizedType
                                                              ? {
                                                                ...t,
                                                                categories:
                                                                  t.categories.map(
                                                                    (c) =>
                                                                      c.id ===
                                                                        category.id
                                                                        ? {
                                                                          ...c,
                                                                          parameters:
                                                                            c.parameters.filter(
                                                                              (
                                                                                p
                                                                              ) =>
                                                                                p.id !==
                                                                                parameter.id
                                                                            ),
                                                                        }
                                                                        : c
                                                                  ),
                                                              }
                                                              : t
                                                        );
                                                      setStagedTemplate(
                                                        newTemplates
                                                      );
                                                      setConfirmDeleteParameterId(
                                                        null
                                                      );
                                                    }}
                                                  >
                                                    מחק
                                                  </Button>
                                                  <Button
                                                    variant="outline-secondary"
                                                    style={{
                                                      fontSize: "0.85rem",
                                                      padding:
                                                        "0.25rem 0.75rem",
                                                      borderRadius: "6px",
                                                      minWidth: 56,
                                                    }}
                                                    onClick={() =>
                                                      setConfirmDeleteParameterId(
                                                        null
                                                      )
                                                    }
                                                  >
                                                    ביטול
                                                  </Button>
                                                </span>
                                              </span>
                                            ) : (
                                              <>
                                                <Button
                                                  variant="outline-primary"
                                                  size="sm"
                                                  onClick={() => {
                                                    setEditingParameter(
                                                      parameter.id
                                                    );
                                                    setEditingParameterName(
                                                      parameter.name
                                                    );
                                                    setEditingParameterType(
                                                      parameter.inputType
                                                    );
                                                  }}
                                                  style={{
                                                    fontSize: "0.85rem",
                                                    borderRadius: "6px",
                                                    minWidth: "140px",
                                                    whiteSpace: "nowrap",
                                                  }}
                                                >
                                                  שנה נתוני פרמטר{" "}
                                                  <FaEdit
                                                    style={{ marginRight: 6 }}
                                                  />
                                                </Button>
                                                <Button
                                                  variant="outline-danger"
                                                  size="sm"
                                                  onClick={() =>
                                                    setConfirmDeleteParameterId(
                                                      parameter.id
                                                    )
                                                  }
                                                  style={{
                                                    fontSize: "0.85rem",
                                                    borderRadius: "6px",
                                                    minWidth: "140px",
                                                    whiteSpace: "nowrap",
                                                  }}
                                                >
                                                  מחק פרמטר{" "}
                                                  <FaTrashIcon
                                                    style={{ marginRight: 6 }}
                                                  />
                                                </Button>
                                              </>
                                            )}
                                          </div>
                                        </div>
                                      )}
                                    </Draggable>
                                  ))}
                                  {provided.placeholder}
                                </div>
                              )}
                            </Droppable>
                          </DragDropContext>
                        ) : (
                          // View-only mode: just render parameters as before
                          <>
                            {/* Table-like header for parameters - only if parameters exist */}
                            {categories.map((category) =>
                              category.parameters.length > 0 ? (
                                <React.Fragment key={category.id}>
                                  <div
                                    className="d-flex align-items-center mb-2 px-2"
                                    style={{
                                      fontWeight: 700,
                                      fontSize: "0.98rem",
                                      color: "#6c757d",
                                      background: "transparent",
                                      minHeight: "32px",
                                    }}
                                  >
                                    <div
                                      style={{ flex: 1, textAlign: "right" }}
                                    >
                                      שם פרמטר
                                    </div>
                                    <div
                                      style={{ flex: 1, textAlign: "right" }}
                                    >
                                      סוג מידע
                                    </div>
                                  </div>
                                  {category.parameters.map((parameter) => (
                                    <div
                                      key={parameter.id}
                                      className="parameter-item d-flex align-items-center mb-2 p-2 rounded-3"
                                      style={{
                                        backgroundColor: paramBg,
                                        transition: "all 0.2s ease",
                                        minHeight: "44px",
                                      }}
                                    >
                                      <div
                                        style={{
                                          flex: 1,
                                          textAlign: "right",
                                          display: "flex",
                                          alignItems: "center",
                                        }}
                                      >
                                        <span
                                          className="me-2"
                                          style={{
                                            fontWeight: "800",
                                            marginLeft: "10px",
                                          }}
                                        >
                                          {parameter.name}
                                        </span>
                                      </div>
                                      <div
                                        style={{
                                          flex: 1,
                                          textAlign: "center",
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                        }}
                                      >
                                        <small
                                          className="text-muted"
                                          style={{
                                            width: "100%",
                                            textAlign: "right",
                                          }}
                                        >
                                          {parameter.inputType === "scale"
                                            ? "סקאלה"
                                            : parameter.inputType === "number"
                                              ? "מספר"
                                              : parameter.inputType === "yesOrNo"
                                                ? "כן/לא"
                                                : "טקסט"}
                                        </small>
                                      </div>
                                    </div>
                                  ))}
                                </React.Fragment>
                              ) : null
                            )}
                          </>
                        )}
                      </div>
                    </Card.Body>
                  </Card>
                ))}
              </div>
            )}
          </Card.Body>
        </Card>
      );
    };

    return (
      <div className="evaluation-settings-container" dir="rtl">
        {!showTemplateBuilder ? (
          <div
            className="d-flex flex-column align-items-center justify-content-center"
            style={{ minHeight: "300px", width: "100%" }}
          >
            {evaluationTemplates.length > 0 ? (
              <div style={{ width: "100%" }}>
                <div
                  className="d-flex justify-content-end align-items-center mb-3"
                  style={{ gap: 12 }}
                >
                  {!showArchivedTemplates && (
                    <Button
                      variant="primary"
                      className="rounded-pill fw-bold px-4 py-2"
                      style={{ fontSize: "1.08rem", minWidth: 180 }}
                      onClick={handleCreateNewTemplate}
                    >
                      <FaPlus style={{ marginLeft: 8 }} />
                      צור תבנית משוב חדשה
                    </Button>
                  )}
                  <Button
                    variant={showArchivedTemplates ? "secondary" : "warning"}
                    className="rounded-pill fw-bold px-4 py-2"
                    style={{ fontSize: "1.08rem", minWidth: 180 }}
                    onClick={() =>
                      setShowArchivedTemplates(!showArchivedTemplates)
                    }
                  >
                    {showArchivedTemplates ? (
                      <FaBox style={{ marginLeft: 8 }} />
                    ) : (
                      <FaTrash style={{ marginLeft: 8 }} />
                    )}
                    {showArchivedTemplates ? "הצג תבניות פעילות" : "הצג ארכיון"}
                  </Button>
                </div>
                <style>{`
                                    .template-cards-column {
                                        display: flex;
                                        flex-direction: column;
                                        gap: 20px;
                                        margin-bottom: 24px;
                                    }
                                    .template-card-compact {
                                        border-radius: 16px;
                                        border: none;
                                        background: linear-gradient(120deg, #e3f0ff 0%, #f8fafd 100%);
                                        box-shadow: 0 2px 12px rgba(25, 118, 210, 0.08);
                                        transition: box-shadow 0.2s, transform 0.15s;
                                        min-height: 140px;
                                        position: relative;
                                        overflow: hidden;
                                    }
                                    .template-card-compact:hover {
                                        box-shadow: 0 6px 24px rgba(25, 118, 210, 0.18);
                                        transform: translateY(-2px) scale(1.02);
                                    }
                                    .template-card-header {
                                        font-weight: 800;
                                        font-size: 1.22rem;
                                        color: #1976d2;
                                        margin-bottom: 8px;
                                        display: flex;
                                        align-items: center;
                                        gap: 10px;
                                    }
                                    .template-card-details-row {
                                        display: flex;
                                        flex-wrap: wrap;
                                        gap: 24px 32px;
                                        margin-bottom: 6px;
                                    }
                                    .template-card-detail {
                                        font-size: 1.04rem;
                                        color: #333;
                                        min-width: 160px;
                                        font-weight: 500;
                                        display: flex;
                                        align-items: center;
                                        gap: 6px;
                                    }
                                    .template-card-detail-label {
                                        color: #1976d2;
                                        font-weight: 700;
                                        margin-left: 4px;
                                    }
                                    .template-card-counts {
                                        font-size: 1.01rem;
                                        color: #555;
                                        margin-bottom: 8px;
                                        font-weight: 500;
                                    }
                                    .template-card-actions {
                                        display: flex;
                                        gap: 10px;
                                        margin-top: 8px;
                                    }
                                    .template-action-btn {
                                        border-radius: 999px;
                                        font-weight: 700;
                                        font-size: 1.02rem;
                                        padding: 7px 18px;
                                        box-shadow: 0 1px 4px rgba(25, 118, 210, 0.10);
                                        transition: background 0.18s, color 0.18s, transform 0.12s;
                                        display: flex;
                                        align-items: center;
                                        gap: 6px;
                                    }
                                    .template-action-btn.view {
                                        background: #1976d2;
                                        color: #fff;
                                        border: none;
                                    }
                                    .template-action-btn.view:hover {
                                        background: #1251a3;
                                        color: #fff;
                                        transform: translateY(-1px) scale(1.04);
                                    }
                                    .template-action-btn.duplicate {
                                        background: #43a047;
                                        color: #fff;
                                        border: none;
                                    }
                                    .template-action-btn.duplicate:hover {
                                        background: #2e7031;
                                        color: #fff;
                                        transform: translateY(-1px) scale(1.04);
                                    }
                                    .template-action-btn.archive {
                                        background: #ff9800;
                                        color: #fff;
                                        border: none;
                                    }
                                    .template-action-btn.archive:hover {
                                        background: #c66900;
                                        color: #fff;
                                        transform: translateY(-1px) scale(1.04);
                                    }
                                `}</style>
                <div className="template-cards-column">
                  {displayedTemplates
                    .sort((a, b) => {
                      // Handle both Date objects and Firebase Timestamps
                      const getTime = (date) => {
                        if (date instanceof Date) return date.getTime();
                        if (date?.seconds)
                          return (
                            date.seconds * 1000 +
                            (date.nanoseconds || 0) / 1000000
                          );
                        return 0; // fallback
                      };
                      return getTime(b.createdAt) - getTime(a.createdAt);
                    })
                    .map((template) => {
                      // Format date as dd/mm/yyyy hh:mm
                      let createdDate;
                      if (template.createdAt instanceof Date) {
                        const d = template.createdAt;
                        createdDate = `${d
                          .getDate()
                          .toString()
                          .padStart(2, "0")}/${(d.getMonth() + 1)
                            .toString()
                            .padStart(2, "0")}/${d.getFullYear()} ${d
                              .getHours()
                              .toString()
                              .padStart(2, "0")}:${d
                                .getMinutes()
                                .toString()
                                .padStart(2, "0")}`;
                      } else if (template.createdAt?.seconds) {
                        const d = new Date(template.createdAt.seconds * 1000);
                        createdDate = `${d
                          .getDate()
                          .toString()
                          .padStart(2, "0")}/${(d.getMonth() + 1)
                            .toString()
                            .padStart(2, "0")}/${d.getFullYear()} ${d
                              .getHours()
                              .toString()
                              .padStart(2, "0")}:${d
                                .getMinutes()
                                .toString()
                                .padStart(2, "0")}`;
                      } else {
                        createdDate = "";
                      }
                      return (
                        <Card
                          key={template.templateId}
                          className="template-card-compact shadow-sm hover-shadow"
                        >
                          <Card.Body
                            className="p-3 d-flex flex-column justify-content-between h-100"
                            style={{ direction: "rtl" }}
                          >
                            <div>
                              <div className="template-card-header">
                                <FaChartBar
                                  style={{ color: "#1976d2", fontSize: 22 }}
                                />
                                {template.name}
                              </div>
                              <div className="template-card-details-row">
                                <div className="template-card-detail">
                                  <span className="template-card-detail-label">
                                    גרסה:
                                  </span>{" "}
                                  {template.version}
                                </div>
                                <div className="template-card-detail">
                                  <span className="template-card-detail-label">
                                    נוצר ב:
                                  </span>{" "}
                                  {createdDate}
                                </div>
                              </div>
                              <div className="template-card-details-row">
                                <div className="template-card-detail">
                                  <span className="template-card-detail-label">
                                    קטגוריות מאמן:
                                  </span>{" "}
                                  {template.coachTemplate.categories.length}
                                </div>
                                <div className="template-card-detail">
                                  <span className="template-card-detail-label">
                                    קטגוריות ספורטאי:
                                  </span>{" "}
                                  {template.athleteTemplate.categories.length}
                                </div>
                              </div>
                            </div>
                            <div className="template-card-actions">
                              <Button
                                className="template-action-btn view"
                                onClick={() => handleViewTemplate(template)}
                              >
                                <FaEye /> צפה
                              </Button>
                              <Button
                                className="template-action-btn duplicate"
                                onClick={() =>
                                  handleDuplicateTemplate(template)
                                }
                              >
                                <FaCopy /> שכפל
                              </Button>
                              <Button
                                className="template-action-btn archive"
                                onClick={() =>
                                  handleArchiveTemplate(
                                    template,
                                    !showArchivedTemplates
                                  )
                                }
                                disabled={isUpdatingArchive}
                              >
                                {showArchivedTemplates ? (
                                  <FaBox />
                                ) : (
                                  <FaTrash />
                                )}
                                {showArchivedTemplates
                                  ? "הוצא מארכיון"
                                  : "העבר לארכיון"}
                              </Button>
                            </div>
                          </Card.Body>
                        </Card>
                      );
                    })}
                </div>
              </div>
            ) : (
              <div className="text-center">
                {!showArchivedTemplates && (
                  <TipCard>
                    רוצה ללמוד על מה זה תבניות משוב? חזור למסך הקודם ולחץ על
                    כפתור העזרה בפינה השמאלית העליונה של המסך!
                  </TipCard>
                )}
                <FaChartBar
                  size={64}
                  className="text-muted mb-3"
                  style={{ opacity: 0.5 }}
                />
                <h4 className="mb-3">
                  {showArchivedTemplates
                    ? "אין תבניות בארכיון"
                    : "אין תבניות פעילות"}
                </h4>
                <p className="text-muted mb-4">
                  {showArchivedTemplates
                    ? "תבניות פעילות יופיעו כאן לאחר שמירה בארכיון"
                    : "צור תבנית חדשה כדי להתחיל"}
                </p>
                {!showArchivedTemplates && (
                  <Button
                    variant="primary"
                    className="rounded-pill fw-bold px-4 py-2 mt-2"
                    style={{ fontSize: "1.08rem", minWidth: 180 }}
                    onClick={handleCreateNewTemplate}
                  >
                    <FaPlus style={{ marginLeft: 8 }} />
                    צור תבנית משוב חדשה
                  </Button>
                )}
              </div>
            )}
          </div>
        ) : (
          <>
            <div className="d-flex justify-content-end mb-4">
              <Button
                onClick={() => {
                  if (hasUnsavedChanges && !isViewOnly) {
                    setShowTemplateBuilderUnsavedModal(true);
                  } else {
                    setShowTemplateBuilder(false);
                    setTemplateName("");
                    setIsViewOnly(false);
                  }
                }}
                className="rounded-pill d-flex align-items-center justify-content-start fw-bold"
                style={{
                  fontSize: "1.25rem",
                  padding: "12px 36px",
                  color: "#444",
                  background: "#e8e8e8",
                  border: "none",
                  boxShadow: "none",
                  marginBottom: "8px",
                  marginRight: "0",
                  transition: "all 0.2s",
                  width: "fit-content",
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.background = "#e0e0e0";
                  e.currentTarget.style.transform = "translateY(-2px)";
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = "#f1f1f1";
                  e.currentTarget.style.transform = "none";
                }}
              >
                <FaArrowRight className="ms-2" style={{ fontSize: "1.2em" }} />
                <span style={{ fontWeight: 700 }}>חזרה</span>
              </Button>
            </div>
            <Card
              className="mb-4 shadow-sm"
              style={{ borderRadius: "12px", border: "none" }}
            >
              <Card.Body className="p-4">
                <Form.Group>
                  <h4 className="mb-0 text-primary" style={{ fontWeight: 800 }}>
                    {isViewOnly ? "צפייה בתבנית" : "שם התבנית*"}
                  </h4>
                  <Form.Control
                    type="text"
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                    placeholder="הזן שם לתבנית"
                    className="border-0 shadow-sm"
                    style={{
                      height: "45px",
                      borderRadius: "8px",
                      fontSize: "1rem",
                      direction: "rtl",
                      textAlign: "right",
                      background: "#fff",
                      transition: "box-shadow 0.2s",
                    }}
                    disabled={isViewOnly}
                  />
                </Form.Group>
              </Card.Body>
            </Card>
            {renderEvaluationSection("coach")}
            <div
              className="my-4"
              style={{ height: "1px", backgroundColor: "#e9ecef" }}
            />
            {renderEvaluationSection("student")}
            {!isViewOnly && (
              <div className="mt-4 d-flex justify-content-center">
                <Button
                  variant="success"
                  size="lg"
                  onClick={handleSaveTemplate}
                  disabled={
                    isSavingTemplate ||
                    (() => {
                      // Must have template name
                      if (!templateName.trim()) return true;
                      // Must have at least one coach category and at least one parameter in any coach category
                      const coach = stagedTemplate.find(
                        (t) => t.templateType === "coach"
                      );
                      if (
                        !coach ||
                        !coach.categories ||
                        coach.categories.length === 0
                      )
                        return true;
                      const hasParam = coach.categories.some(
                        (cat) => cat.parameters && cat.parameters.length > 0
                      );
                      if (!hasParam) return true;
                      return false;
                    })()
                  }
                  className="rounded-pill px-5 py-2 shadow-sm fw-bold d-flex align-items-center justify-content-center"
                  style={{
                    fontSize: "1.1rem",
                    boxShadow: "0 2px 8px rgba(0,180,80,0.12)",
                    transition: "all 0.2s",
                    width: "fit-content",
                    opacity: isSavingTemplate
                      ? 0.7
                      : (() => {
                        // Must have template name
                        if (!templateName.trim()) return 0.6;
                        // Must have at least one coach category and at least one parameter in any coach category
                        const coach = stagedTemplate.find(
                          (t) => t.templateType === "coach"
                        );
                        if (
                          !coach ||
                          !coach.categories ||
                          coach.categories.length === 0
                        )
                          return 0.6;
                        const hasParam = coach.categories.some(
                          (cat) => cat.parameters && cat.parameters.length > 0
                        );
                        if (!hasParam) return 0.6;
                        return 1;
                      })(),
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.transform = "none";
                  }}
                >
                  {isSavingTemplate ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm ms-2"
                        role="status"
                        aria-hidden="true"
                        dir="rtl"
                      ></span>
                      שומר...
                    </>
                  ) : (
                    <>
                      <FaCheck className="ms-2" />
                      שמור תבנית
                    </>
                  )}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  // Add a function to handle new template creation
  const handleCreateNewTemplate = () => {
    setStagedTemplate([]);
    setTemplateName("");
    setIsViewOnly(false);
    setShowTemplateBuilder(true);
  };

  // Add state for editing category name
  const [editingCategoryName, setEditingCategoryName] = useState("");

  // Add function to handle category name change
  const handleCategoryNameChange = (categoryId, newName, type) => {
    if (!newName.trim()) return;

    const normalizedType = type === "athlete" ? "student" : type;
    const newTemplates = stagedTemplate.map((t) => {
      if (t.templateType === normalizedType) {
        return {
          ...t,
          categories: t.categories.map((c) =>
            c.id === categoryId
              ? {
                ...c,
                name: newName.trim(),
              }
              : c
          ),
        };
      }
      return t;
    });

    setStagedTemplate(newTemplates);
    setEditingCategory(null);
    setEditingCategoryName("");
  };

  // Add function to start editing category name
  const handleStartEditingCategory = (category) => {
    setEditingCategory(category.id);
    setEditingCategoryName(category.name);
  };

  // Add function to handle category duplication
  const handleDuplicateCategory = (category, type) => {
    // Find which template this category belongs to
    const templateType = stagedTemplate.find((template) =>
      template.categories.some((cat) => cat.id === category.id)
    )?.templateType;

    const normalizedType =
      templateType === "athlete" ? "student" : templateType;

    // Create a deep copy of the category with new IDs
    const newCategory = {
      id: Date.now().toString() + "_" + Math.random().toString(36).substr(2, 9),
      name: `${category.name} (העתק)`,
      parameters: category.parameters.map((param) => ({
        ...param,
        id:
          Date.now().toString() + "_" + Math.random().toString(36).substr(2, 9),
      })),
    };

    // Initialize stagedTemplate if it's empty
    let newTemplates = [...stagedTemplate];

    if (newTemplates.length === 0) {
      newTemplates = [
        {
          templateType: "coach",
          categories: [],
        },
        {
          templateType: "student",
          categories: [],
        },
      ];
    }

    // Update the staged template
    newTemplates = newTemplates.map((template) => {
      if (template.templateType === normalizedType) {
        return {
          ...template,
          categories: [...template.categories, newCategory],
        };
      }
      return template;
    });

    // If the template type doesn't exist in stagedTemplate, add it
    if (!newTemplates.some((t) => t.templateType === normalizedType)) {
      newTemplates.push({
        templateType: normalizedType,
        categories: [newCategory],
      });
    }

    setStagedTemplate(newTemplates);
  };

  // Add new state for tracking default template updates
  // const [isUpdatingDefault, setIsUpdatingDefault] = useState(false);

  // Add new state for tracking archive template updates
  const [isUpdatingArchive, setIsUpdatingArchive] = useState(false);

  // Add state for showing archived templates
  const [showArchivedTemplates, setShowArchivedTemplates] = useState(false);

  // Add function to handle setting default template
  // const handleSetDefaultTemplate = async (template) => {
  //   if (template.isDefaultTemplate) return;

  //   setIsUpdatingDefault(true);
  //   try {
  //     await updateDefaultTemplate(userID, template.templateId);

  //     // Update local state
  //     const updatedTemplates = evaluationTemplates.map((t) => ({
  //       ...t,
  //       isDefaultTemplate: t.templateId === template.templateId,
  //     }));
  //     setEvaluationTemplates(updatedTemplates);

  //     activateAlert(
  //       "success",
  //       "תבנית ראשית עודכנה",
  //       "התבנית הוגדרה כתבנית ראשית בהצלחה"
  //     );
  //   } catch (error) {
  //     activateAlert(
  //       "danger",
  //       "שגיאה",
  //       "אירעה שגיאה בהגדרת התבנית הראשית. נסה שוב."
  //     );
  //   } finally {
  //     setIsUpdatingDefault(false);
  //   }
  // };

  // Add function to handle template archiving
  const handleArchiveTemplate = async (template, isArchived) => {
    setIsUpdatingArchive(true);
    try {
      const result = await updateTemplateArchiveStatus(
        userID,
        template.templateId,
        isArchived
      );
      if (result) {
        // Update local state only on success
        const updatedTemplates = evaluationTemplates.map((t) =>
          t.templateId === template.templateId
            ? { ...t, isArchived: isArchived }
            : t
        );
        setEvaluationTemplates(updatedTemplates);
        activateAlert(
          "success",
          isArchived ? "תבנית נשמרה בארכיון" : "תבנית הוצאה מהארכיון",
          isArchived
            ? "התבנית נשמרה בארכיון בהצלחה"
            : "התבנית הוצאה מהארכיון בהצלחה"
        );
      } else {
        activateAlert(
          "danger",
          "שגיאה",
          "אירעה שגיאה בעדכון סטטוס הארכיון. נסה שוב."
        );
      }
    } catch (error) {
      activateAlert(
        "danger",
        "שגיאה",
        "אירעה שגיאה בעדכון סטטוס הארכיון. נסה שוב."
      );
    } finally {
      setIsUpdatingArchive(false);
    }
  };

  // Add useEffect for scrolling to new category
  useEffect(() => {
    if (lastCreatedCategoryId) {
      const element = document.getElementById(
        `category-${lastCreatedCategoryId}`
      );
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
        setLastCreatedCategoryId(null);
      }
    }
  }, [lastCreatedCategoryId]);

  // --- DRAG AND DROP HANDLER FOR PARAMETERS ---
  // Helper to reorder array
  function reorder(list, startIndex, endIndex) {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
  }

  // Handler for drag end (parameters)
  const handleParameterDragEnd = (result, categoryId, templateType) => {
    if (!result.destination) return;
    const normalizedType =
      templateType === "athlete" ? "student" : templateType;
    setStagedTemplate((prevTemplates) =>
      prevTemplates.map((t) => {
        if (t.templateType === normalizedType) {
          return {
            ...t,
            categories: t.categories.map((cat) => {
              if (cat.id === categoryId) {
                return {
                  ...cat,
                  parameters: reorder(
                    cat.parameters,
                    result.source.index,
                    result.destination.index
                  ),
                };
              }
              return cat;
            }),
          };
        }
        return t;
      })
    );
  };

  // 1. Add new state for view-only mode and the viewed template
  const [isViewingTemplate, setIsViewingTemplate] = useState(false);
  const [viewedTemplate, setViewedTemplate] = useState(null);

  // 2. Rewrite handleViewTemplate to set these states
  const handleViewTemplate = (template) => {
    function cloneCategories(categories) {
      return (categories || []).map((cat) => ({
        ...cat,
        parameters: Array.isArray(cat.parameters)
          ? cat.parameters.map((param) => ({ ...param }))
          : [],
      }));
    }
    setViewedTemplate({
      name: template.name,
      coachTemplate: {
        categories: cloneCategories(template.coachTemplate?.categories || []),
      },
      athleteTemplate: {
        categories: cloneCategories(template.athleteTemplate?.categories || []),
      },
    });
    setIsViewingTemplate(true);
  };

  // 3. In the main render logic, add:
  // If isViewingTemplate, render the view-only UI (mirroring the edit UI, but all fields disabled/hidden)
  // Otherwise, render the normal modal content

  // 4. Add a function to exit view mode
  const handleCloseViewTemplate = () => {
    setIsViewingTemplate(false);
    setViewedTemplate(null);
  };

  // 5. Add a new renderViewTemplate function/component
  function renderViewTemplate() {
    if (!viewedTemplate) return null;
    return (
      <div dir="rtl">
        <div className="d-flex justify-content-end mb-4">
          <Button
            onClick={handleCloseViewTemplate}
            className="rounded-pill d-flex align-items-center justify-content-start fw-bold"
            style={{
              fontSize: "1.25rem",
              padding: "12px 36px",
              color: "#444",
              background: "#e8e8e8",
              border: "none",
              boxShadow: "none",
              marginBottom: "8px",
              marginRight: "0",
              transition: "all 0.2s",
              width: "fit-content",
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.background = "#e0e0e0";
              e.currentTarget.style.transform = "translateY(-2px)";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = "#f1f1f1";
              e.currentTarget.style.transform = "none";
            }}
          >
            <FaArrowRight className="ms-2" style={{ fontSize: "1.2em" }} />
            <span style={{ fontWeight: 700 }}>חזרה</span>
          </Button>
        </div>
        <Card
          className="mb-4 shadow-sm"
          style={{ borderRadius: "12px", border: "none" }}
        >
          <Card.Body className="p-4">
            <Form.Group>
              <h4 className="mb-0 text-primary" style={{ fontWeight: "600" }}>
                צפייה בתבנית
              </h4>
              <Form.Control
                type="text"
                value={viewedTemplate.name}
                disabled
                className="border-0 shadow-sm"
                style={{
                  height: "45px",
                  borderRadius: "8px",
                  fontSize: "1rem",
                  direction: "rtl",
                  textAlign: "right",
                  background: "#f5f5f5",
                  transition: "box-shadow 0.2s",
                }}
              />
            </Form.Group>
          </Card.Body>
        </Card>
        {["coach", "athlete"].map((type) => {
          const templateSection = viewedTemplate[type + "Template"];
          const categories =
            templateSection && Array.isArray(templateSection.categories)
              ? templateSection.categories
              : [];
          const typeLabel = type === "coach" ? "מאמן" : "ספורטאי";
          const categoryBg = type === "coach" ? "#eaf4fb" : "#fffbe6";
          const paramBg = type === "coach" ? "#d0e6f7" : "#fff3b0";
          return (
            <Card
              key={type}
              className="evaluation-settings mb-4 shadow-sm"
              style={{
                borderRadius: "12px",
                border: "none",
                background: categoryBg,
              }}
            >
              <Card.Header className="bg-white border-0 pt-4 pb-2">
                <h4 className="mb-0 text-primary" style={{ fontWeight: 800 }}>
                  משוב {typeLabel}
                </h4>
                <h6 className="mb-0 text-primary" style={{ fontWeight: 600 }}>
                  {typeLabel === "מאמן"
                    ? "לפחות קטגוריה אחת ופרמטר אחד"
                    : "לא חובה"}
                </h6>
              </Card.Header>
              <Card.Body>
                {categories.length === 0 ? (
                  <div
                    className="empty-state d-flex flex-column align-items-center justify-content-center"
                    style={{ minHeight: "180px", width: "100%" }}
                  >
                    <FaChartBar
                      size={48}
                      className="text-muted opacity-50 mb-3"
                    />
                    <h5 className="text-muted mb-1" style={{ fontWeight: 500 }}>
                      אין קטגוריות מוגדרות
                    </h5>
                    <p
                      className="text-muted mb-0"
                      style={{ fontSize: "0.95rem", fontWeight: 400 }}
                    >
                      הזן שם קטגוריה חדשה כדי להתחיל
                    </p>
                  </div>
                ) : (
                  <div className="categories-section">
                    {categories.map((category) => (
                      <Card
                        key={category.id}
                        id={`category-${category.id}`}
                        className="mb-3 border-0 shadow-sm"
                        style={{
                          borderRadius: "10px",
                          transition: "all 0.2s ease",
                          ":hover": { transform: "translateY(-2px)" },
                        }}
                      >
                        <Card.Body className="p-3">
                          <div className="d-flex justify-content-start align-items-center mb-3">
                            <h6
                              className="mb-0"
                              style={{ fontWeight: "800", marginLeft: "20px" }}
                            >
                              קטגוריית {category.name}
                            </h6>
                          </div>
                          <div className="parameters-section">
                            {category.parameters.length > 0 && (
                              <div
                                className="d-flex align-items-center mb-2 px-2"
                                style={{
                                  fontWeight: 700,
                                  fontSize: "0.98rem",
                                  color: "#6c757d",
                                  background: "transparent",
                                  minHeight: "32px",
                                }}
                              >
                                <div style={{ flex: 1, textAlign: "right" }}>
                                  שם פרמטר
                                </div>
                                <div style={{ flex: 1, textAlign: "right" }}>
                                  סוג מידע
                                </div>
                              </div>
                            )}
                            {category.parameters.map((parameter) => (
                              <div
                                key={parameter.id}
                                className="parameter-item d-flex align-items-center mb-2 p-2 rounded-3"
                                style={{
                                  backgroundColor: paramBg,
                                  transition: "all 0.2s ease",
                                  minHeight: "44px",
                                }}
                              >
                                <div
                                  style={{
                                    flex: 1,
                                    textAlign: "right",
                                    display: "flex",
                                    alignItems: "center",
                                  }}
                                >
                                  <span
                                    className="me-2"
                                    style={{
                                      fontWeight: "800",
                                      marginLeft: "10px",
                                    }}
                                  >
                                    {parameter.name}
                                  </span>
                                </div>
                                <div
                                  style={{
                                    flex: 1,
                                    textAlign: "center",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  <small
                                    className="text-muted"
                                    style={{
                                      width: "100%",
                                      textAlign: "right",
                                    }}
                                  >
                                    {parameter.inputType === "scale"
                                      ? "סקאלה"
                                      : parameter.inputType === "number"
                                        ? "מספר"
                                        : parameter.inputType === "yesOrNo"
                                          ? "כן/לא"
                                          : "טקסט"}
                                  </small>
                                </div>
                              </div>
                            ))}
                          </div>
                        </Card.Body>
                      </Card>
                    ))}
                  </div>
                )}
              </Card.Body>
            </Card>
          );
        })}
      </div>
    );
  }

  // 6. In the main render, use:
  // {isViewingTemplate ? renderViewTemplate() : ...existing modal content...}
  return (
    <>
      <CustomModal
        isOpen={isOpen}
        onClose={onClose}
        title="ניהול תבניות משוב"
        width={60}
        height={90}
        footerButtons={[]}
      >
        <CustomModal
          isOpen={showTemplateBuilderUnsavedModal}
          onClose={() => setShowTemplateBuilderUnsavedModal(false)}
          title="שינויים לא נשמרו"
          width={32}
          height={40}
          footerButtons={[
            {
              title: "צא בלי לשמור",
              style: "red",
              onClick: () => {
                setShowTemplateBuilderUnsavedModal(false);
                setShowTemplateBuilder(false);
                setTemplateName("");
                setStagedTemplate([]);
                setIsViewOnly(false);
              },
            },
            {
              title: "בטל",
              style: "grey",
              onClick: () => setShowTemplateBuilderUnsavedModal(false),
            },
          ]}
          style={{ zIndex: 1301, position: "fixed" }} // Ensure above main modal
          backdropStyle={{ zIndex: 1300 }} // Ensure backdrop is above main modal
          disableBackgroundScroll={true} // Prevent scroll on background modal
        >
          <div style={{ textAlign: "center", padding: 12 }}>
            <div
              style={{
                fontWeight: 600,
                fontSize: "1.15rem",
                marginBottom: 16,
                direction: "rtl",
              }}
            >
              יש לך שינויים שלא נשמרו. האם לצאת בלי לשמור?
            </div>
          </div>
        </CustomModal>
        {isViewingTemplate ? renderViewTemplate() : renderEvaluationSettings()}
      </CustomModal>
    </>
  );
};

export default FeedbackTemplatesModal;
